import React, { createContext, useContext, useEffect, useState } from 'react';
import { getBaseUrl } from '../config/api';
import {
  DEFAULT_IDLE_TIMEOUT_MINUTES,
  DEFAULT_IDLE_WARNING_MINUTES,
} from '../constants/idleTimeout';

interface FeatureFlags {
  stylistApproval: boolean;
  /**
   * Kill-switch for the stylist web inactivity-logout feature.
   * Defaults to true (enabled) when the backend does not yet expose this field.
   * Set to false via /config/features to disable the timer site-wide.
   */
  stylistIdleLogoutEnabled: boolean;
  /**
   * Kill-switch for Running Late. Defaults to true when the backend does not
   * yet expose this field. Set to false via /config/features to hide the flow.
   */
  runningLateEnabled: boolean;
  /** Total idle time (minutes) before a stylist is logged out. */
  idleTimeoutMinutes: number;
  /** Minutes before timeout to show the warning modal. */
  warningMinutes: number;
}

const defaultFlags: FeatureFlags = {
  stylistApproval: false,
  stylistIdleLogoutEnabled: true, // safe default: enabled until explicitly disabled
  runningLateEnabled: true,
  idleTimeoutMinutes: DEFAULT_IDLE_TIMEOUT_MINUTES,
  warningMinutes: DEFAULT_IDLE_WARNING_MINUTES,
};

const STORAGE_KEY = 'lookbel:featureFlags';

function readCachedFlags(): FeatureFlags {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaultFlags, ...JSON.parse(raw) };
  } catch {
    // Ignore parse errors — corrupt cache
  }
  return defaultFlags;
}

const FeatureFlagsContext = createContext<FeatureFlags>(defaultFlags);

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Seed from cache so components render with correct values immediately
  const [flags, setFlags] = useState<FeatureFlags>(readCachedFlags);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${getBaseUrl()}/config/features`, { signal: controller.signal })
      .then((res) => res.json())
      .then((json) => {
        if (json?.success && json?.data) {
          const next: FeatureFlags = {
            stylistApproval: json.data.stylistApproval ?? false,
            // Default true: if backend hasn't added this field yet, keep the feature on
            stylistIdleLogoutEnabled: json.data.stylistIdleLogoutEnabled ?? true,
            runningLateEnabled: json.data.runningLateEnabled !== false,
            idleTimeoutMinutes: json.data.idleTimeoutMinutes ?? DEFAULT_IDLE_TIMEOUT_MINUTES,
            warningMinutes: json.data.warningMinutes ?? DEFAULT_IDLE_WARNING_MINUTES,
          };
          setFlags(next);
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          } catch {
            // Storage quota exceeded — ignore
          }
        }
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name !== 'AbortError') {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[FeatureFlags] Failed to fetch flags, using cached/defaults', err);
          }
        }
      });

    return () => controller.abort();
  }, []);

  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlags => useContext(FeatureFlagsContext);

export default FeatureFlagsContext;
